import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentsApi, promptsApi } from "@/services/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { RoleGate } from "@/components/RoleGate";
import { PlanLimitModal } from "@/components/billing/PlanLimitModal";
import { useOrgRole } from "@/hooks/useOrgRole";
import { useEntitlements } from "@/hooks/useEntitlements";
import { events as analyticsEvents } from "@/lib/analytics";
import { Bot, Plus, Pencil, History, Phone, Key, Mic, AlertTriangle, Sliders } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { groupsApi } from "@/services/api";
import { VALID_MODELS, DEFAULT_AGENT_MODEL } from "@/lib/aiModels";

const CUSTOM_PROVIDERS = [
  { value: "openai", label: "OpenAI" },
  { value: "google", label: "Google (Gemini)" },
  { value: "anthropic", label: "Anthropic (Claude)" },
  { value: "mistral", label: "Mistral" },
];

type ModelStrength = "adaptive" | "preferred" | "override";

interface AgentForm {
  name: string;
  whatsapp_number: string;
  group_id: string | null;
  model: string;
  model_strength: ModelStrength;
  system_prompt: string;
  temperature: number;
  active: boolean;
  custom_api_key: string;
  custom_provider: string;
  custom_base_url: string;
  jarvis_voice_id: string;
  jarvis_voice_provider: string;
  jarvis_persona_description: string;
  prompt_id: string | null;
}

const defaultForm: AgentForm = {
  name: "",
  whatsapp_number: "",
  group_id: null,
  model: DEFAULT_AGENT_MODEL,
  model_strength: "adaptive",
  system_prompt: "",
  temperature: 0.7,
  active: true,
  custom_api_key: "",
  custom_provider: "",
  custom_base_url: "",
  jarvis_voice_id: "",
  jarvis_voice_provider: "",
  jarvis_persona_description: "",
  prompt_id: null,
};

const VOICE_PROVIDERS = [
  { value: "elevenlabs", label: "ElevenLabs" },
  { value: "openai", label: "OpenAI TTS" },
];

export default function Agents() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { orgId } = useOrgRole();
  const entitlements = useEntitlements();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<AgentForm>(defaultForm);
  const [historyAgentId, setHistoryAgentId] = useState<string | null>(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [conflictState, setConflictState] = useState<{ targetId: string; targetName: string; groupName: string; conflicting: { id: string; name: string }[] } | null>(null);

  const { data: agents = [], isLoading } = useQuery({ queryKey: ["agents"], queryFn: agentsApi.list });
  const { data: groups = [] } = useQuery({ queryKey: ["groups"], queryFn: groupsApi.list });
  const { data: prompts = [] } = useQuery({ queryKey: ["prompts"], queryFn: () => promptsApi.list() });
  const { data: history = [] } = useQuery({
    queryKey: ["agent-history", historyAgentId],
    queryFn: () => agentsApi.history(historyAgentId!),
    enabled: !!historyAgentId,
  });

  const createMut = useMutation({
    mutationFn: (data: AgentForm & { org_id: string }) => agentsApi.create(data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["agents"] });
      closeDialog();
      toast({ title: "Persona criada" });
      analyticsEvents.agentCreated(data?.id);
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AgentForm> }) => agentsApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agents"] }); closeDialog(); toast({ title: "Persona atualizada" }); },
  });

  const deleteMut = useMutation({
    mutationFn: agentsApi.delete,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agents"] }); toast({ title: "Persona excluída" }); },
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => agentsApi.update(id, { active }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["agents"] });
      toast({ title: vars.active ? "Persona ativada" : "Persona desativada" });
    },
    onError: (e: any) => toast({ title: "Falha ao atualizar", description: e.message, variant: "destructive" }),
  });

  const handleToggleActive = (agent: any, nextActive: boolean) => {
    if (!nextActive) {
      toggleMut.mutate({ id: agent.id, active: false });
      return;
    }
    if (agent.group_id) {
      const conflicting = agents.filter(
        (a: any) => a.id !== agent.id && a.active && a.group_id === agent.group_id,
      );
      if (conflicting.length > 0) {
        const groupName = groups.find((g: any) => g.id === agent.group_id)?.name || "este grupo";
        setConflictState({
          targetId: agent.id,
          targetName: agent.name,
          groupName,
          conflicting: conflicting.map((c: any) => ({ id: c.id, name: c.name })),
        });
        return;
      }
    }
    toggleMut.mutate({ id: agent.id, active: true });
  };

  const resolveConflictKeepNew = async () => {
    if (!conflictState) return;
    for (const c of conflictState.conflicting) {
      await agentsApi.update(c.id, { active: false });
    }
    await agentsApi.update(conflictState.targetId, { active: true });
    qc.invalidateQueries({ queryKey: ["agents"] });
    toast({ title: `"${conflictState.targetName}" ativada`, description: `Outras personas do grupo "${conflictState.groupName}" foram desativadas.` });
    setConflictState(null);
  };

  const closeDialog = () => { setDialogOpen(false); setEditId(null); setForm(defaultForm); };

  const openCreate = () => {
    if (!entitlements.canCreate("agents")) {
      setShowLimitModal(true);
      return;
    }
    setHistoryAgentId(null);
    setForm(defaultForm);
    setEditId(null);
    setDialogOpen(true);
  };

  const openEdit = (agent: any) => {
    setHistoryAgentId(null);
    setForm({
      name: agent.name,
      whatsapp_number: agent.whatsapp_number,
      group_id: agent.group_id,
      model: agent.model,
      model_strength: (agent.model_strength as ModelStrength) || "adaptive",
      system_prompt: agent.system_prompt,
      temperature: Number(agent.temperature),
      active: agent.active,
      custom_api_key: agent.custom_api_key || "",
      custom_provider: agent.custom_provider || "",
      custom_base_url: agent.custom_base_url || "",
      jarvis_voice_id: agent.jarvis_voice_id || "",
      jarvis_voice_provider: agent.jarvis_voice_provider || "",
      jarvis_persona_description: agent.jarvis_persona_description || "",
      prompt_id: agent.prompt_id ?? null,
    });
    setEditId(agent.id);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) { toast({ title: "Nome obrigatório", variant: "destructive" }); return; }
    if (form.custom_provider && !form.custom_api_key.trim()) {
      toast({ title: "Chave API obrigatória quando provedor é selecionado", variant: "destructive" });
      return;
    }
    const payload = {
      ...form,
      custom_api_key: form.custom_api_key.trim() || null,
      custom_provider: form.custom_provider || null,
      custom_base_url: form.custom_base_url.trim() || null,
      jarvis_voice_id: form.jarvis_voice_id.trim() || null,
      jarvis_voice_provider: form.jarvis_voice_provider || null,
      jarvis_persona_description: form.jarvis_persona_description.trim() || null,
    };
    if (editId) {
      updateMut.mutate({ id: editId, data: payload });
    } else {
      if (!orgId) { toast({ title: "Organização não encontrada", variant: "destructive" }); return; }
      createMut.mutate({ ...payload, org_id: orgId });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Personas IA</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure personalidades e comportamentos da IA. O modelo é uma preferência — o roteamento adaptativo pode otimizar automaticamente.</p>
        </div>
        <RoleGate permission="agents.create">
          <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Nova Persona</Button>
        </RoleGate>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-52 w-full" />)}
        </div>
      ) : agents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Nenhuma persona configurada</p>
            <p className="text-sm mt-1">Crie uma persona para definir o comportamento da IA nos grupos</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {agents.map((agent: any) => {
            const groupName = groups.find((g: any) => g.id === agent.group_id)?.name;
            const modelLabel = VALID_MODELS.find((m) => m.id === agent.model)?.label || agent.model;
            return (
              <Card key={agent.id} className="hover:shadow-lg hover:scale-[1.02] hover:-translate-y-0.5 transition-all duration-300 ease-out">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div className="flex items-center gap-2">
                    <Bot className="w-5 h-5 text-primary" />
                    <CardTitle className="text-base">{agent.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={agent.active ? "default" : "secondary"}>{agent.active ? "Ativo" : "Inativo"}</Badge>
                    <RoleGate permission="agents.create">
                      <Switch
                        checked={agent.active}
                        disabled={toggleMut.isPending}
                        onCheckedChange={(v) => handleToggleActive(agent, v)}
                        aria-label={agent.active ? "Desativar persona" : "Ativar persona"}
                      />
                    </RoleGate>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {agent.whatsapp_number && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-3 h-3" /> {agent.whatsapp_number}
                    </div>
                  )}
                  {groupName && <Badge variant="outline" className="text-xs">{groupName}</Badge>}
                  <div className="text-xs text-muted-foreground">Modelo preferido: {modelLabel}</div>
                  {agent.custom_provider && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Key className="w-3 h-3" /> Chave própria ({agent.custom_provider})
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">Temperatura: {Number(agent.temperature).toFixed(1)} · v{agent.version}</div>
                  {agent.system_prompt && (
                    <p className="text-xs text-muted-foreground line-clamp-2">{agent.system_prompt}</p>
                  )}
                  <div className="flex gap-1 pt-2 flex-wrap">
                    <RoleGate permission="agents.create">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(agent)} title="Editar"><Pencil className="w-3 h-3" /></Button>
                    </RoleGate>
                    <Button variant="ghost" size="sm" onClick={() => { closeDialog(); setHistoryAgentId(agent.id); }} title="Histórico"><History className="w-3 h-3" /></Button>
                    <RoleGate permission="agents.create">
                      <Button variant="ghost" size="sm" asChild title="Personalidade">
                        <a href={`/agents/${agent.id}/personality`}><Sliders className="w-3 h-3" /></a>
                      </Button>
                    </RoleGate>
                    <RoleGate permission="agents.delete">
                      <ConfirmDeleteDialog itemName={agent.name} itemType="persona" onConfirm={() => deleteMut.mutate(agent.id)} triggerSize="sm" triggerClassName="text-destructive" />
                    </RoleGate>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={() => closeDialog()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Persona" : "Nova Persona"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Nome</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Bot Resumos Diários" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Número WhatsApp</label>
              <Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="5511999998888" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Grupo</label>
              <Select value={form.group_id || ""} onValueChange={(v) => setForm({ ...form, group_id: v || null })}>
                <SelectTrigger><SelectValue placeholder="Selecione um grupo" /></SelectTrigger>
                <SelectContent>
                  {groups.map((g: any) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Modelo preferido</label>
              <Select value={form.model} onValueChange={(v) => setForm({ ...form, model: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VALID_MODELS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Estratégia do modelo</label>
              <Select value={form.model_strength} onValueChange={(v) => setForm({ ...form, model_strength: v as ModelStrength })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="adaptive">Adaptativo (recomendado) — gateway escolhe o melhor modelo por intenção/complexidade</SelectItem>
                  <SelectItem value="preferred">Preferido — usa o escolhido salvo se houver problema (latência/custo)</SelectItem>
                  <SelectItem value="override">Override — sempre usa o modelo escolhido, mesmo que não seja ótimo</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {form.model_strength === "adaptive" && "Cada mensagem é roteada pelo gateway adaptativo. Mais barato e rápido na maioria dos casos."}
                {form.model_strength === "preferred" && "O modelo preferido é tentado primeiro; o gateway pode rebaixar para fallback em SLA ou custo."}
                {form.model_strength === "override" && "Travado no modelo selecionado — útil para auditoria/teste, pode ser caro ou lento."}
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Temperatura: {form.temperature.toFixed(1)}</label>
              <Slider value={[form.temperature]} onValueChange={([v]) => setForm({ ...form, temperature: v })} min={0} max={2} step={0.1} />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Preciso</span><span>Criativo</span>
              </div>
              {form.temperature > 1.2 && (
                <div className="text-xs rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-3 py-2">
                  ⚠ Temperatura &gt; 1.2 aumenta risco de alucinação e respostas instáveis. Recomendado: 0.5–1.0.
                </div>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Vincular Prompt do Catálogo (versionado)</label>
              <Select
                value={form.prompt_id ?? "none"}
                onValueChange={(v) => {
                  const newId = v === "none" ? null : v;
                  const p = prompts.find((x: any) => x.id === newId);
                  setForm({
                    ...form,
                    prompt_id: newId,
                    system_prompt: p ? (p.template as string) : form.system_prompt,
                  });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Sem vínculo (usar prompt manual abaixo)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem vínculo (usar prompt manual)</SelectItem>
                  {prompts.map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} <span className="text-muted-foreground ml-1">(v{p.version})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.prompt_id ? (
                <p className="text-xs text-emerald-500">✓ Vinculado — runtime usará a versão atual deste prompt automaticamente.</p>
              ) : prompts.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum prompt no catálogo. Cadastre em Prompt Library.</p>
              ) : (
                <p className="text-xs text-muted-foreground">Sem vínculo — o prompt manual abaixo será usado.</p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Prompt do Sistema</label>
              <Textarea value={form.system_prompt} onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} rows={6} placeholder="Instruções para o agente de IA..." />
            </div>
            {/* Jarvis Voice Section */}
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div className="flex items-center gap-2">
                <Mic className="w-4 h-4 text-primary" />
                <label className="text-sm font-medium text-foreground">Voz Jarvis (modo voz)</label>
              </div>
              <p className="text-xs text-muted-foreground">
                Quando esta persona for usada no Jarvis (voz em tempo real), use esta voz e descrição específica.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground">Provedor de voz</label>
                  <Select
                    value={form.jarvis_voice_provider || "none"}
                    onValueChange={(v) => setForm({ ...form, jarvis_voice_provider: v === "none" ? "" : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Padrão da org" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Padrão da org</SelectItem>
                      {VOICE_PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground">Voice ID</label>
                  <Input
                    value={form.jarvis_voice_id}
                    onChange={(e) => setForm({ ...form, jarvis_voice_id: e.target.value })}
                    placeholder="Ex: 9BWtsMINqrJLrRacOk9x"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Descrição da persona (Jarvis)</label>
                <Textarea
                  value={form.jarvis_persona_description}
                  onChange={(e) => setForm({ ...form, jarvis_persona_description: e.target.value })}
                  rows={3}
                  placeholder="Opcional. Sobrescreve o prompt de sistema apenas no modo voz. Ex: 'Você é um copiloto sênior, fala objetivo e calmo, máximo 2 frases.'"
                />
              </div>
            </div>

            {/* Custom API Key Section */}
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-primary" />
                <label className="text-sm font-medium text-foreground">Chave API Própria (opcional)</label>
              </div>
              <p className="text-xs text-muted-foreground">
                Configure uma chave API de um provedor pago para usar modelos premium diretamente. Quando vazio, o gateway padrão será usado.
              </p>
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Provedor</label>
                <Select value={form.custom_provider || "none"} onValueChange={(v) => setForm({ ...form, custom_provider: v === "none" ? "" : v, ...(v === "none" ? { custom_api_key: "", custom_base_url: "" } : {}) })}>
                  <SelectTrigger><SelectValue placeholder="Selecione o provedor..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum (usar gateway padrão)</SelectItem>
                    {CUSTOM_PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.custom_provider && (
                <>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-foreground">Chave API</label>
                    <Input
                      type="password"
                      value={form.custom_api_key}
                      onChange={(e) => setForm({ ...form, custom_api_key: e.target.value })}
                      placeholder="sk-... ou AIza..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-foreground">URL Base (opcional)</label>
                    <Input
                      value={form.custom_base_url}
                      onChange={(e) => setForm({ ...form, custom_base_url: e.target.value })}
                      placeholder="Ex: https://api.openai.com/v1"
                    />
                    <p className="text-xs text-muted-foreground">Deixe em branco para usar o endpoint padrão do provedor.</p>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm text-foreground">Ativo</label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending} className="w-full">
              {(createMut.isPending || updateMut.isPending) ? "Salvando..." : editId ? "Salvar Alterações" : "Criar Persona"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyAgentId} onOpenChange={() => setHistoryAgentId(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Histórico de Prompts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhum histórico ainda</p>
            ) : (
              history.map((h: any) => (
                <Card key={h.id}>
                  <CardContent className="pt-4 space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>v{h.version}</span>
                      <span>{new Date(h.created_at).toLocaleString("pt-BR")}</span>
                    </div>
                    <p className="text-xs">Modelo: {h.model} · Temp: {Number(h.temperature).toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground line-clamp-4 mt-1">{h.system_prompt}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Plan Limit Modal */}
      <PlanLimitModal
        open={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        entity="agents"
        current={entitlements.currentAgents}
        max={entitlements.maxAgents}
      />

      {/* Conflict: múltiplas personas ativas no mesmo grupo */}
      <AlertDialog open={!!conflictState} onOpenChange={(o) => !o && setConflictState(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              Conflito de personas no grupo
            </AlertDialogTitle>
            <AlertDialogDescription>
              O grupo <strong>{conflictState?.groupName}</strong> já possui outra persona ativa:
              <ul className="list-disc pl-5 mt-2 text-foreground">
                {conflictState?.conflicting.map((c) => <li key={c.id}>{c.name}</li>)}
              </ul>
              <p className="mt-3">
                Apenas uma persona pode estar ativa por grupo. Deseja desativar a(s) outra(s) e ativar <strong>{conflictState?.targetName}</strong>?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={resolveConflictKeepNew}>
              Ativar "{conflictState?.targetName}"
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}